import { useEffect, useMemo, useState } from 'react';
import { BTN_PRIMARY, BTN_SECONDARY_SM } from '../buttonStyles';
import { DevAnyTimeTag } from '../liveBooking/BookingScreen';
import { CalendarPicker } from '../liveBooking/CalendarPicker';
import { FOOTNOTE } from './surfaces';
import { useLiveCalls } from '../../hooks/useLiveCalls';
import { useLiveConfig } from '../../hooks/useLiveConfig';
import { useToast } from '../../hooks/useToast';
import { getAvailableSlotsForCoach } from '../../services/coachAvailability';
import { getLiveBookingContext, liveLessonsErrorMessage, proposeLiveLessonTimes } from '../../services/liveLessons';
import { leadMinutesOf } from '../../utils/bookingWindow';
import { whenLine } from '../../utils/liveCalendar';
import {
  dayTimes, gridMinutes, LESSON_TIMES_MAX, originalStart, requestButtonLabel, requestCounter, requestDays, requestTitle, togglePick,
} from '../../utils/lessonTimes';
import type { LiveCallBooking } from '../../types/liveCall';
import Sheet from '../Sheet';
import { SentTimesSheet } from './SentTimesSheet';
import { runBehind } from '../../services/optimistic';
import { addLocalChange, removeLocalChange } from '../../utils/actionCardStore';
import { firstNameOf } from '../../utils/liveLessonText';
import { requestLiveNewTime } from '../../services/liveV2';


/**
 * The coach's Request a New Time (decision 113; booking-contracts.md 6),
 * in the pane it was opened from, the plan page's or the lesson page's:
 * any time on the server's grid from 6:00 AM to 11:00 PM, any day from today
 * to 6 days after the lesson, up to three, sent as one card in the thread
 * with proposeLiveLessonTimes. Picked on the booking calendar (components.md
 * 6, decision 156); times the server would refuse are absent.
 * Titled "Pick up to three times that work for you, so Ana has options" with "1 of 3" beside it, and one is
 * enough to send (165 as amended). The player's request for other times (row 4, side "player") is the same
 * sheet on the coach's open times, "so Ryan has options".
 */
export function CoachRequestTimes({ booking, tz, playerFirst, onClose, inSheet = false, onSent, side = 'coach', coachFirst = '', holdId, lessonName }: {
  booking: LiveCallBooking; tz: string; playerFirst: string; onClose: (sent?: boolean) => void;
  /** Who sends: the coach on the grid, or the player on the coach's open times. */
  side?: 'coach' | 'player';
  coachFirst?: string;
  /** A held date not yet booked (140): the proposal is the hold's (contracts 15 and 16), `booking` its stand-in. */
  holdId?: string;
  /** The lesson as the server's lines name it ("Lesson 4"), for the waiting card shown at once. */
  lessonName?: string;
  /** Inside a sheet: the owner shows the confirmation in its place (one sheet at a time, SH-2); null takes it back. */
  onSent?: (times: string[] | null) => void;
  /** Inside a Sheet (components.md 10): its bar carries the title and Cancel. */
  inSheet?: boolean;
}) {
  const { showError } = useToast();
  const { upcoming } = useLiveCalls('coach');
  const [now] = useState(() => Date.now());
  const original = originalStart(booking as LiveCallBooking & { rescheduleHistory?: Array<{ fromStartAt?: string }> });
  // The grid and the lead time: config/live's (booking-contracts.md 10), the apps' one source for the slot size.
  const config = useLiveConfig();
  // DEV ONLY: LIVE_DEV_ANY_TIME. Every day from today, only the past and a clash greyed.
  const anyTime = config?.devAnyTime === true;
  const days = useMemo(() => requestDays(now, original, tz, anyTime), [now, original, tz, anyTime]);
  const [picks, setPicks] = useState<string[]>([]);
  const [fallback, setFallback] = useState<{ lead: number; step: number } | null>(null);
  // The coach's open times, for the player's side: only those can be sent.
  const [openSlots, setOpenSlots] = useState<Set<string> | null>(null);

  // Until config/live is there: the booking context's lead, and the grid read off the coach's open times.
  useEffect(() => {
    let live = true;
    void Promise.all([
      getLiveBookingContext(booking.coachId).catch(() => null),
      getAvailableSlotsForCoach(booking.coachId).catch(() => []),
    ]).then(([ctx, slots]) => {
      if (!live) return;
      setFallback({ lead: leadMinutesOf(ctx?.bookingWindow), step: gridMinutes(ctx?.bookingWindow.slotMinutes, slots.map((s) => s.startAt)) });
      setOpenSlots(new Set(slots.map((s) => new Date(s.startAt).toISOString())));
    });
    return () => { live = false; };
  }, [booking.coachId]);

  const rules = config ? { lead: config.minLeadMinutes, step: gridMinutes(config.slotMinutes, []) } : fallback;
  const busy = upcoming.filter((c) => c.id !== booking.id && c.status === 'confirmed')
    .map((c) => ({ startAt: c.scheduledStartAt, minutes: c.durationMinutes ?? 45 }));
  // The grid's times for every day in the window (components.md 6): only those the server would take; past, inside the
  // lead, clashing and the lesson's own time are absent, not greyed. DEV ONLY: LIVE_DEV_ANY_TIME keeps the refused ones greyed.
  const [zone, setZone] = useState(tz);
  const grid = rules ? days.flatMap((d) => dayTimes({ day: d, tz: zone, step: rules.step, nowMs: now,
    leadMinutes: rules.lead, original, current: booking.scheduledStartAt, minutes: booking.durationMinutes ?? 45, busy, anyTime })) : [];
  const player = side === 'player';
  const times = grid.filter((t) => (anyTime || t.state === 'open') && (!player || !!openSlots?.has(new Date(t.startAt).toISOString())))
    .map((t) => ({ id: t.startAt, startAt: t.startAt }));
  const title = requestTitle(player ? coachFirst || 'your coach' : playerFirst);
  const counter = <span className={`${FOOTNOTE} tabular-nums`} data-request-counter>{requestCounter(picks.length, LESSON_TIMES_MAX)}</span>;
  const greyed = anyTime ? new Set(grid.filter((t) => t.state !== 'open').map((t) => t.startAt)) : undefined;
  const full = picks.length >= LESSON_TIMES_MAX;

  // Sent at once (160, 5a; 157): the confirmation shows the moment the times go, the call runs behind it (a
  // retry is safe: a new proposal replaces the open one), and a failure brings the picker back with a toast.
  const [sent, setSent] = useState<string[] | null>(null);
  const send = () => {
    if (!picks.length) return;
    const times = [...picks].sort();
    if (onSent) onSent(times); else setSent(times);
    // The waiting card at once, at the top of the lesson's pages, until the record (the walk of 4 Nov, BOOKING 7).
    const localId = `local:${side === 'player' ? 'asked' : 'times'}:${booking.id}:${Date.now()}`;
    if (!holdId) addLocalChange({ id: localId, kind: side === 'player' ? 'asked' : 'times', by: side, coachFirst: coachFirst || firstNameOf(booking.coachName, 'Your coach'),
      playerFirst, lesson: lessonName, from: booking.scheduledStartAt, to: times, status: 'open', at: new Date().toISOString(), bookingId: booking.id });
    // Row 4 is one call from the player (contracts 16, Ryan's answers): requestLiveNewTime with the starts.
    void runBehind<unknown>(() => (side === 'player'
      ? requestLiveNewTime({ bookingId: booking.id, startAts: times })
      : proposeLiveLessonTimes(holdId ? { holdId, startAts: times } : { bookingId: booking.id, startAts: times }))).catch((err) => {
      if (onSent) onSent(null); else setSent(null);
      removeLocalChange(localId);
      showError(liveLessonsErrorMessage(err, "Couldn't send these times. Try again."));
    });
  };

  return (
    <div data-request-times={booking.id}>
      {/* Done: back to the page at its top, where the waiting card is (the walk of 4 Nov, BOOKING 7). */}
      {sent && <SentTimesSheet playerFirst={playerFirst} times={sent} fromStartAt={booking.scheduledStartAt} tz={zone}
        onDone={() => { onClose(true); requestAnimationFrame(() => window.scrollTo({ top: 0 })); }} />}
      {!inSheet && (
        <div className="flex items-start gap-3">
          <h1 className="min-w-0 flex-1 text-[22px] leading-[28px] font-bold text-db-text-1" data-request-title>{title}</h1>
          <div className="flex flex-shrink-0 items-center gap-3 pt-1">
            {counter}
            <button type="button" onClick={() => onClose()} className={BTN_SECONDARY_SM}>Cancel</button>
          </div>
        </div>
      )}
      {inSheet && <div className="flex justify-end">{counter}</div>}
      {/* DEV ONLY: LIVE_DEV_ANY_TIME. So nobody mistakes it for production. */}
      {anyTime && <DevAnyTimeTag />}

      {/* The calendar picker (components.md 6), up to three times */}
      <div className="mt-6">
        <CalendarPicker days={days} times={times} timeZone={zone} onZoneChange={setZone} state={rules ? 'ready' : 'loading'}
          selectedIds={picks} onPick={(t) => setPicks((p) => togglePick(p, t.startAt))} disabledIds={greyed} nowMs={now} full={full} />
      </div>

      {picks.length > 0 && (
        <p className={`mt-4 ${FOOTNOTE}`} data-request-picks>{picks.map((s) => whenLine(s, zone)).join(' · ')}</p>
      )}
      <button type="button" onClick={send} disabled={!picks.length} className={`${BTN_PRIMARY} mt-4 w-full`} data-request-send>
        {requestButtonLabel(picks.length)}
      </button>
    </div>
  );
}

/**
 * The same, over the page: the thread's way in (the Move chip's Reschedule, "Suggest new times"), and the player's
 * "None of these work" (165, row 4: side "player", the coach's open times, "so Ryan has options").
 */
export function CoachRequestTimesOverlay({ booking, tz, playerFirst, onClose, side = 'coach', coachFirst = '' }: {
  booking: LiveCallBooking; tz: string; playerFirst: string; onClose: () => void;
  side?: 'coach' | 'player'; coachFirst?: string;
}) {
  // Once sent, "Sent to Ana" in the picker's place: one sheet at a time (SH-2).
  const [sent, setSent] = useState<string[] | null>(null);
  const other = side === 'player' ? coachFirst || 'your coach' : playerFirst;
  if (sent) return <SentTimesSheet playerFirst={other} times={sent} fromStartAt={booking.scheduledStartAt} tz={tz} onDone={onClose} />;
  // The sheet (components.md 10): Cancel and the title in its bar, which may take two lines.
  return (
    <Sheet title={requestTitle(other)} onClose={onClose}>
      <div className="pb-4 pt-2"><CoachRequestTimes booking={booking} tz={tz} playerFirst={playerFirst} onClose={onClose} inSheet onSent={setSent}
        side={side} coachFirst={coachFirst} /></div>
    </Sheet>
  );
}
